import { trackEvent } from '../analyticsService';
import { LANDING_EVENTS } from '../events';
import { useLandingVersion } from '@/core/landing/landingVersionContext';
import { useUserStore } from '@/core/store/useUserStore';
import { getNavigationTrigger } from '../navigationTrigger';

export function useLandingAnalytics() {
  const version = useLandingVersion();

  const withLandingDefaults = (payload = {}) => ({
    is_logged_in: useUserStore().isLoggedIn,
    ...payload,
    module_name: 'landing',
    landing_version: version.value
  });

  const trackLandingPageViewed = (payload = {}) => {
    const navigationTrigger = getNavigationTrigger();
    const resolvedSourcePosition =
      payload.source_position ||
      (navigationTrigger === 'back'
        ? 'browser_back'
        : navigationTrigger === 'forward'
          ? 'browser_forward'
          : 'landing_page');
    trackEvent(LANDING_EVENTS.LANDING_PAGE_VIEWED, withLandingDefaults({
      event_label: 'viewed',
      source_position: resolvedSourcePosition,
      element_type: 'page',
      navigation_trigger: navigationTrigger,
      ...payload
    }));
  };

  const trackLandingSectionViewed = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_SECTION_VIEWED, withLandingDefaults({
      event_label: 'section_viewed',
      element_type: 'section',
      content_type: 'landing_section',
      ...payload
    }));
  };

  const trackLandingNavClicked = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_NAV_CLICKED, withLandingDefaults({
      event_label: 'nav_clicked',
      source_position: 'landing_header',
      element_type: 'button',
      method: 'user_click',
      ...payload
    }));
  };

  const trackLandingCtaClicked = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_CTA_CLICKED, withLandingDefaults({
      event_label: 'cta_clicked',
      element_type: 'button',
      method: 'user_click',
      ...payload
    }));
  };

  const trackLandingCtaNavigationCompleted = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_CTA_NAVIGATION_COMPLETED, withLandingDefaults({
      event_label: 'navigation_completed',
      method: 'programmatic',
      ...payload
    }));
  };

  const trackLandingOptionChanged = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_OPTION_CHANGED, withLandingDefaults({
      event_label: 'option_changed',
      method: 'user_click',
      ...payload
    }));
  };

  const trackLandingWaitlistSubmitStarted = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_WAITLIST_SUBMIT_STARTED, withLandingDefaults({
      event_label: 'submit_started',
      source_position: 'landing_waitlist',
      element_name: 'waitlist_form',
      element_type: 'form',
      method: 'user_click',
      ...payload
    }));
  };

  const trackLandingWaitlistSubmitCompleted = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_WAITLIST_SUBMIT_COMPLETED, withLandingDefaults({
      event_label: 'submit_completed',
      source_position: 'landing_waitlist',
      element_name: 'waitlist_form',
      element_type: 'form',
      method: 'user_click',
      ...payload
    }));
  };

  const trackLandingContentClicked = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_CONTENT_CLICKED, withLandingDefaults({
      event_label: 'content_clicked',
      method: 'user_click',
      ...payload
    }));
  };

  const trackLandingPricingLoadCompleted = (payload = {}) => {
    trackEvent(LANDING_EVENTS.LANDING_PRICING_LOAD_COMPLETED, withLandingDefaults({
      event_label: 'pricing_load_completed',
      source_position: 'landing_pricing',
      element_type: 'section',
      method: 'network',
      ...payload
    }));
  };

  return {
    trackLandingPageViewed,
    trackLandingSectionViewed,
    trackLandingNavClicked,
    trackLandingCtaClicked,
    trackLandingCtaNavigationCompleted,
    trackLandingOptionChanged,
    trackLandingWaitlistSubmitStarted,
    trackLandingWaitlistSubmitCompleted,
    trackLandingContentClicked,
    trackLandingPricingLoadCompleted
  };
}
